import React from 'react'
import { useDispatch } from 'react-redux'
import { Formik, Form, Field, ErrorMessage } from 'formik'
import { Alert, Input } from '@wellbeingxltd/fe-lib/components'
import { Button } from 'components'
import { getChangedValues } from 'helpers'
import { useSupplierProfile } from 'hooks/useSupplierProfile'
import SupplierActions from 'store/models/supplier'
import supplierSchema from './validationSchema'
import './styles.scss'

const ProfileListItem = ({ title = '', value = null, children = null }) => (
    <div className="profile-list-item">
        {title && <span className="profile-list-title">{title}</span>}
        {value && <span className="profile-list-value">{value}</span>}
        {children}
    </div>
)

const withErrorMessage = Component => props =>
    (
        <div className="profile-list-item-with-error">
            <Component {...props} />
            <ErrorMessage name={props.fieldName} render={msg => <span className="error">{msg}</span>} />
        </div>
    )

const ProfileListItemWithErrorMessage = withErrorMessage(ProfileListItem)

const Profile = ({ openModal }) => {
    const dispatch = useDispatch()
    const { supplier, loadingUpdateSupplier } = useSupplierProfile()

    const {
        companyName,
        tradingName,
        vatRegNumber,
        companyRegNumber,
        address = [{}],
        primaryContactEmail,
        primaryContactPhone,
        financeEmail,
        financePhone,
        accountsPayableEmail,
    } = supplier
    const { firstLine, secondLine, postCode, city, country } = address[0]

    const initialValues = {
        companyName: companyName || '',
        tradingName: tradingName || '',
        primaryContactEmail: primaryContactEmail || '',
        primaryContactPhone: primaryContactPhone || '',
        financeEmail: financeEmail || '',
        financePhone: financePhone || '',
        vatRegNumber: vatRegNumber || '',
        companyRegNumber: companyRegNumber || '',
        firstLine: firstLine || '',
        secondLine: secondLine || '',
        postCode: postCode || '',
        city: city || '',
        country: country || '',
        accountsPayableEmail: accountsPayableEmail || '',
    }

    const onSubmit = values => {
        const changedValues = getChangedValues(values, initialValues)

        const { firstLine, secondLine, city, postCode, country } = values
        const address = {
            firstLine,
            city,
            postCode,
            country,
        }
        if (secondLine) address.secondLine = secondLine
        const body = {
            address: JSON.stringify(address),
            supplierId: supplier.supplierId,
            ...changedValues,
        }

        delete body.firstLine
        delete body.city
        delete body.postCode
        delete body.country
        delete body.secondLine

        dispatch(
            SupplierActions.updateSupplier(body, (message, type) => {
                Alert(message, type, 10000)
            })
        )
    }

    return (
        <div className="module settings-profile">
            <div className="module-header">
                <h2>Profile</h2>
            </div>
            <Formik validationSchema={supplierSchema} {...{ enableReinitialize: true, initialValues, onSubmit }}>
                {({ touched }) => (
                    <Form>
                        <div className="profile-row">
                            <div className="profile-col profile-col--first">
                                <ProfileListItemWithErrorMessage title="Name*" fieldName="companyName">
                                    <Field name="companyName">
                                        {({ field }) => <Input id="companyName" type="text" styles={{ container: { width: '100%' } }} {...field} />}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="Trading name" fieldName="tradingName">
                                    <Field name="tradingName">
                                        {({ field }) => <Input id="tradingName" type="text" styles={{ container: { width: '100%' } }} {...field} />}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="Contact/Main Login Email*" fieldName="primaryContactEmail">
                                    <Field name="primaryContactEmail">
                                        {({ field, form }) => (
                                            <Input
                                                type="email"
                                                id="primaryContactEmail"
                                                styles={{ container: { width: '100%' } }}
                                                {...field}
                                                onBlurCapture={e => form.setFieldValue(e.target.name, e.target.value.trim().toLowerCase())}
                                            />
                                        )}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="Contact Phone*" fieldName="primaryContactPhone">
                                    <Field name="primaryContactPhone">
                                        {({ field }) => (
                                            <Input id="primaryContactPhone" type="text" styles={{ container: { width: '100%' } }} {...field} />
                                        )}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="Finance Email*" fieldName="financeEmail">
                                    <Field name="financeEmail">
                                        {({ field, form }) => (
                                            <Input
                                                type="email"
                                                id="financeEmail"
                                                styles={{ container: { width: '100%' } }}
                                                {...field}
                                                onBlurCapture={e => form.setFieldValue(e.target.name, e.target.value.trim().toLowerCase())}
                                            />
                                        )}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="Finance Phone*" fieldName="financePhone">
                                    <Field name="financePhone">
                                        {({ field }) => <Input id="financePhone" type="text" styles={{ container: { width: '100%' } }} {...field} />}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="Accounts Payable Email*" fieldName="accountsPayableEmail">
                                    <Field name="accountsPayableEmail">
                                        {({ field, form }) => (
                                            <Input
                                                type="email"
                                                id="accountsPayableEmail"
                                                styles={{ container: { width: '100%' } }}
                                                {...field}
                                                onBlurCapture={e => form.setFieldValue(e.target.name, e.target.value.trim().toLowerCase())}
                                            />
                                        )}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItem title="Password">
                                    <Button type="button" className="settings-button-link" onClick={openModal}>
                                        Change password
                                    </Button>
                                </ProfileListItem>
                            </div>
                            <div className="profile-col">
                                <ProfileListItemWithErrorMessage title="First Line*" fieldName="firstLine">
                                    <Field name="firstLine">
                                        {({ field }) => <Input id="firstLine" type="text" styles={{ container: { width: '100%' } }} {...field} />}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="Second Line" fieldName="secondLine">
                                    <Field name="secondLine">
                                        {({ field }) => <Input id="secondLine" type="text" styles={{ container: { width: '100%' } }} {...field} />}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="Postcode*" fieldName="postCode">
                                    <Field name="postCode">
                                        {({ field }) => <Input id="postCode" type="text" styles={{ container: { width: '100%' } }} {...field} />}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="City*" fieldName="city">
                                    <Field name="city">
                                        {({ field }) => <Input id="city" type="text" styles={{ container: { width: '100%' } }} {...field} />}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="Country*" fieldName="country">
                                    <Field name="country">
                                        {({ field }) => <Input id="country" type="text" styles={{ container: { width: '100%' } }} {...field} />}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="VAT Reg Number*" fieldName="vatRegNumber">
                                    <Field name="vatRegNumber">
                                        {({ field }) => <Input id="vatRegNumber" type="text" styles={{ container: { width: '100%' } }} {...field} />}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                                <ProfileListItemWithErrorMessage title="Company Reg Number*" fieldName="companyRegNumber">
                                    <Field name="companyRegNumber">
                                        {({ field }) => (
                                            <Input id="companyRegNumber" type="text" styles={{ container: { width: '100%' } }} {...field} />
                                        )}
                                    </Field>
                                </ProfileListItemWithErrorMessage>
                            </div>
                        </div>
                        <div className="settings-button-container settings-button-container-submit">
                            <Button
                                type="submit"
                                className="settings-button-link"
                                loading={loadingUpdateSupplier}
                                disabled={!Object.keys(touched).length}
                            >
                                Save
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div>
    )
}

export default Profile
